import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Zap } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Modal } from "./Dialogs";

const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(2)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const OptimizeDialog = ({ project, ed, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/optimize`, { html: ed.serialize(), apply: true });
      setRes(data);
      if (data.converted) { ed.replaceHtml(data.html, { record: true }); toast.success(`${data.converted} imagem(ns) em WebP · −${data.saved_pct}%`); }
      else toast.info("Nada a otimizar: as imagens já estão no formato leve.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Otimizar página" onClose={onClose} testid="optimize-dialog">
      <p className="text-sm text-slate-600">Converte as imagens da página para <strong>WebP</strong> — o formato ultraleve da web — mantendo as dimensões e a definição. Páginas leves carregam mais rápido e convertem melhor.</p>
      <ul className="mt-3 text-xs text-slate-500 space-y-1">
        <li>• Imagens embutidas (base64), da sua biblioteca e de endereços externos</li>
        <li>• Fotos gigantes são reduzidas só se passarem de 2400px de lado</li>
        <li>• GIFs animados e SVGs ficam intactos, e nada é trocado se o WebP não for menor</li>
      </ul>

      {!res && <button data-testid="optimize-run" disabled={busy} onClick={run} className="btn btn-primary w-full justify-center mt-5 disabled:opacity-60">{busy ? <><Loader2 size={15} className="animate-spin" /> Otimizando imagens…</> : <><Zap size={15} /> Otimizar agora</>}</button>}

      {res && (
        <div data-testid="optimize-result" className="mt-5">
          <div className="rounded-2xl border border-slate-200 p-4 flex items-center justify-around text-center">
            <div><div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">antes</div><div data-testid="optimize-before" className="font-display font-bold text-lg">{kb(res.before)}</div></div>
            <div className="text-slate-300">→</div>
            <div><div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">depois</div><div data-testid="optimize-after" className="font-display font-bold text-lg text-emerald-600">{kb(res.after)}</div></div>
            <div><div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">economia</div><div data-testid="optimize-saved" className="font-display font-bold text-lg text-[var(--blue)]">−{res.saved_pct}%</div></div>
          </div>
          <p className="mt-3 text-xs text-slate-500">{res.converted} de {res.found} imagem(ns) convertidas para WebP{res.converted ? " e já aplicadas na página" : ""}.</p>
          <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
            {res.images.map((i, n) => (
              <div key={n} data-testid={`optimize-item-${n}`} className="flex items-center justify-between gap-2 text-[11px] font-mono rounded-lg bg-slate-50 border border-slate-100 px-2 py-1.5">
                <span className="truncate text-slate-500">{i.ref}</span>
                <span className={i.status === "mantida" ? "text-slate-400 shrink-0" : "text-emerald-600 shrink-0"}>{i.status === "mantida" ? "mantida" : i.status === "movida para arquivo" ? `${kb(i.from)} → arquivo` : `${kb(i.from)} → ${kb(i.to)}`}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="optimize-again" onClick={() => setRes(null)} className="btn btn-ghost flex-1 justify-center">Rodar de novo</button>
            <button data-testid="optimize-close" onClick={onClose} className="btn btn-primary flex-1 justify-center">Pronto</button>
          </div>
        </div>
      )}
    </Modal>
  );
};
